// === MODIFICATEURS D'ÉLITES ===
// Mutations procédurales appliquées aux ennemis élites à partir de l'Acte 2.
// Chaque élite reçoit au plus 1 modificateur, tiré au hasard dans le pool.
// Objectif : varier les combats late-game au-delà du simple scaling HP/ATK/DEF
// (feedback playtest v0.31.4 : "combats plus longs, pas plus intéressants").

export type EliteModifierId =
  | 'regen'
  | 'shield_phase'
  | 'enraged'
  | 'thorns'
  | 'revive'
  | 'resistant';

export interface EliteModifierDefinition {
  id: EliteModifierId;
  name: string;
  emoji: string;
  description: string;
  /** Couleur du contour ajouté sur le sprite (hex Phaser) */
  accentColor: number;
}

export const ELITE_MODIFIERS: Record<EliteModifierId, EliteModifierDefinition> = {
  regen: {
    id: 'regen',
    name: 'Régénération',
    emoji: '💚',
    description: '+2 HP à chaque début de tour',
    accentColor: 0x2ecc71,
  },
  shield_phase: {
    id: 'shield_phase',
    name: 'Bouclier',
    emoji: '🛡️',
    description: '-50% dégâts reçus tant que HP > 50%',
    accentColor: 0x3498db,
  },
  enraged: {
    id: 'enraged',
    name: 'Enragé',
    emoji: '💢',
    description: '+2 ATK dès que HP < 30%',
    accentColor: 0xe74c3c,
  },
  thorns: {
    id: 'thorns',
    name: 'Épines',
    emoji: '🌵',
    description: 'Renvoie 1 dégât aux attaquants en mêlée',
    accentColor: 0x8e44ad,
  },
  revive: {
    id: 'revive',
    name: 'Résurrection',
    emoji: '💀',
    description: 'À la mort, revient une fois à 50% HP',
    accentColor: 0xf39c12,
  },
  resistant: {
    id: 'resistant',
    name: 'Résistant',
    emoji: '⛨',
    description: 'Ignore 1 dégât sur chaque coup reçu (min 1)',
    accentColor: 0x7f8c8d,
  },
};

/** Pool des IDs pour le tirage aléatoire. */
const MODIFIER_POOL: EliteModifierId[] = [
  'regen',
  'shield_phase',
  'enraged',
  'thorns',
  'revive',
  'resistant',
];

/**
 * Tire un modificateur au hasard pour un ennemi élite.
 * Renvoie `null` si on choisit volontairement de ne pas appliquer de modif
 * (20% de chance pour préserver un peu de variance).
 */
export function rollEliteModifier(rng: () => number = Math.random): EliteModifierId | null {
  if (rng() < 0.2) return null;
  const idx = Math.floor(rng() * MODIFIER_POOL.length);
  return MODIFIER_POOL[idx];
}

/**
 * Certains ennemis ont déjà des passifs qui dupliquent un modificateur.
 * On évite d'appliquer un modificateur redondant (ex. `cursed` a déjà un regen).
 */
export function isModifierRedundant(enemyType: string, modifier: EliteModifierId): boolean {
  // Le Noyé Maudit se régénère naturellement
  if (enemyType === 'cursed' && modifier === 'regen') return true;
  // Le Baril Maudit (bomber) meurt en explosion — pas de résurrection (incohérent avec sa mécanique)
  if (enemyType === 'bomber' && modifier === 'revive') return true;
  // Les boss n'ont jamais de modif (leur kit est déjà défini)
  if (enemyType.startsWith('boss')) return true;
  return false;
}
